import { z } from 'zod'

import {
  AdditionalOption,
  BasicOption,
  PropertyType,
  ZRoomDetail,
  ZRoomPosition,
} from '@/entities/room/@x/map'
import { convertToArray, ZCoordinates } from '@/shared/lib'

import {
  PyeongSize,
  RoomCount,
  RoomDiscount,
  PopularOption,
  Floor,
} from './filter'

export const ZMapBounds = z.object({
  swLat: z.number(),
  swLng: z.number(),
  neLat: z.number(),
  neLng: z.number(),
})

export const ZMapZoomLevel = z.number()

export const ZLocationMarkerType = z.enum(['REGION', 'SUBWAY', 'LANDMARK'])

export const ZLocationType = z.enum(['STATE', 'PROVINCE', 'TOWN'])

export const ZSubwayLine = z.object({
  name: z.string(),
  color: z.string(),
  displayName: z.string(),
})

export const ZLandmarkMarker = z
  .object({
    name: z.string(),
    nextZoomLevel: z.number().nullable(),
  })
  .merge(ZCoordinates)

export const ZSubwayMarker = z
  .object({
    name: z.string(),
    nextZoomLevel: z.number().nullable(),
    subwayLines: z.array(ZSubwayLine),
  })
  .merge(ZCoordinates)

export const ZRegionMarker = z
  .object({
    name: z.string(),
    nextZoomLevel: z.number().nullable(),
    regionType: ZLocationType,
    fullName: z.string(),
  })
  .merge(ZCoordinates)

export const ZRoomMarker = z
  .object({
    rid: z.number(),
    feePerWeek: z.number(),
    hasLike: z.boolean(),
    sameGpsCnt: z.number(),
  })
  .merge(ZCoordinates)

export const ZRoomCountMarker = z
  .object({
    sameGpsCnt: z.number(),
    nextZoomLevel: z.number().nullable(),
  })
  .merge(ZCoordinates)

export const ZSearchFilter = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  minUsingFee: z.coerce.number().nullable(),
  maxUsingFee: z.coerce.number().nullable(),
  roomCounts: z.preprocess(convertToArray, z.array(z.nativeEnum(RoomCount))),
  propertyTypes: z.preprocess(
    convertToArray,
    z.array(z.nativeEnum(PropertyType)),
  ),
  pyeongSizes: z.preprocess(convertToArray, z.array(z.nativeEnum(PyeongSize))),
  floors: z.preprocess(convertToArray, z.array(z.nativeEnum(Floor))),
  popularOptions: z.preprocess(
    convertToArray,
    z.array(z.nativeEnum(PopularOption)),
  ),
  basicOptions: z.preprocess(
    convertToArray,
    z.array(z.nativeEnum(BasicOption)),
  ),
  additionalOptions: z.preprocess(
    convertToArray,
    z.array(z.nativeEnum(AdditionalOption)),
  ),
  roomDiscounts: z.preprocess(
    convertToArray,
    z.array(z.nativeEnum(RoomDiscount)),
  ),
})

export const ZSearchRoom = ZRoomDetail.pick({
  rid: true,
  roomName: true,
  addrLot: true,
  addrStreet: true,
  propertyType: true,
  usingFee: true,
  mgmtFee: true,
  pyeongSize: true,
  roomCnt: true,
  bathroomCnt: true,
  cookroomCnt: true,
  sittingroomCnt: true,
  like: true,
})
  .extend({
    state: z.string(),
    province: z.string(),
    town: z.string(),
    picMain: z.string().nullable(),
    recoType1: z.boolean(),
    recoType2: z.boolean(),
    longtermDiscountPer: z.number(),
    earlyDiscountAmount: z.number(),
    isNew: z.boolean(),
    isSuperHost: z.boolean(),
  })
  .merge(ZRoomPosition)

export type TMapBounds = z.infer<typeof ZMapBounds>
export type TMapZoomLevel = z.infer<typeof ZMapZoomLevel>

export type TSearchFilter = z.infer<typeof ZSearchFilter>

export type TSubwayLine = z.infer<typeof ZSubwayLine>
export type TLandmarkMarker = z.infer<typeof ZLandmarkMarker>
export type TSubwayMarker = z.infer<typeof ZSubwayMarker>
export type TRegionMarker = z.infer<typeof ZRegionMarker>
export type TRoomMarker = z.infer<typeof ZRoomMarker>
export type TRoomCountMarker = z.infer<typeof ZRoomCountMarker>
export type TLocationMarkerType = z.infer<typeof ZLocationMarkerType>

export type TKeywordMarker = TLandmarkMarker | TSubwayMarker | TRegionMarker

export type TSearchRoom = z.infer<typeof ZSearchRoom>
